/**
 * Реестры территории на каноне: массивы, хозяйства, контуры, культуры, циклы.
 *
 * Это ОПИСЬ ЗЕМЛИ, а не операционный журнал: строки реестра в каноне живут
 * постранично и ограничены районом, поэтому их можно обойти целиком и отдать
 * экрану в форме прототипа. Журнал обязательств так обходить нельзя — там
 * считает сервер (см. `dataset.ts`).
 *
 * ЕДИНСТВЕННЫЙ ОБХОД РЕЕСТРА КОНТУРОВ. Связи «хозяйство ↔ массив» в списке
 * `/actors` нет: канон отдаёт её там, где она живёт, — в строке контура
 * (`massif` + `land_user`). Поэтому `massif_ids` и счётчики контуров у
 * хозяйства и массива выводятся ИЗ ТОГО ЖЕ обхода, что и сам реестр контуров.
 * Иначе у одного экрана оказались бы два несогласованных знаменателя.
 *
 * ЧЕГО В КАНОНЕ НЕТ, ТО НЕ ВЫДУМЫВАЕТСЯ. Размещение культуры по строке
 * реестра контуров не приходит (`crop_2026`/`crop_2025w` = `null`, что на
 * экране уже читается как «размещение неизвестно»), категория земли у строки
 * контура не объявлена (`unknown`), руководителя хозяйства канон не хранит
 * (пустая строка), центра у реестровой записи нет (`NO_CENTER`).
 */

import { api, type ActorRow, type ContourCycleOut, type ContourIndexRow, type QueryParams } from '../../../api/client';
import { canonCrops } from './crops';
import { cycleTitleOfKey } from '../../lib/format';
import type {
  ContourMeta,
  Crop,
  Cycle,
  Farm,
  LandCategory,
  Massif,
  Meta,
} from '../../data/types';
import type { ReadResult } from '../types';
import {
  attempt,
  contextKey,
  failure,
  haOrOmit,
  NO_CENTER,
  once,
  pageThrough,
  toHa,
  type DatasetReader,
  type Paged,
} from './dataset';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  district: {
    id: 'data.registry.districtName',
    defaultMessage: 'Бука',
    description: 'Название района пилота (имя собственное, как оно пишется на карте и в шапке)',
  },
  farmNo: {
    id: 'data.registry.farmNo',
    defaultMessage: 'Хозяйство {id}',
    description: 'Название хозяйства в реестре, когда сервер имя не назвал; {id} — номер хозяйства',
  },
  snapshot: {
    id: 'data.registry.snapshot',
    defaultMessage: '{file} ({kind}, правила {rules})',
    description: 'Источник среза в описи данных: {file} — имя файла выгрузки, {kind} — вид источника, {rules} — версия правил',
  },
  noSnapshot: {
    id: 'data.registry.noSnapshot',
    defaultMessage: 'снимок базы района не объявлен',
    description: 'Источник среза в описи данных, когда снимок не объявлен (строчная фраза)',
  },
  arableUnstated: {
    id: 'data.registry.arableUnstated',
    defaultMessage: 'пашня не названа у {count, plural, one {# контура} other {# контуров}}: в итог они не вошли',
    description: 'Оговорка к итогу гектаров: у части контуров пашня не названа; {count} — число контуров',
  },
  whatMeta: {
    id: 'data.registry.what.meta',
    defaultMessage: 'опись среза',
    description: 'Что не загрузилось (в тексте отказа сервера): опись среза данных',
  },
  whatMassifs: {
    id: 'data.registry.what.massifs',
    defaultMessage: 'реестр массивов',
    description: 'Что не загрузилось (в тексте отказа сервера): реестр массивов',
  },
  whatFarms: {
    id: 'data.registry.what.farms',
    defaultMessage: 'реестр хозяйств',
    description: 'Что не загрузилось (в тексте отказа сервера): реестр хозяйств',
  },
  whatContours: {
    id: 'data.registry.what.contours',
    defaultMessage: 'реестр контуров',
    description: 'Что не загрузилось (в тексте отказа сервера): реестр контуров',
  },
  whatCycles: {
    id: 'data.registry.what.cycles',
    defaultMessage: 'районные агроциклы',
    description: 'Что не загрузилось (в тексте отказа сервера): агроциклы района',
  },
});


/**
 * Район пилота. Это НЕ данные: канон обслуживает один район, и его имя стоит
 * в хлебных крошках приложения (`usePageTrail`) независимо от источника.
 * Складывать его в ответ сервера было бы вторым местом правды.
 */
const districtName = (): string => tr(m.district);


/** Категория земли у строки реестра контуров канона не объявлена. */
const UNKNOWN_CATEGORY: LandCategory = 'unknown';

/** Строка справочника с циклом, развёрнутым из списка циклов ответа. */
type RegistryContour = Omit<ContourIndexRow, 'cycle'> & { cycle: ContourCycleOut | null };

interface RegistryFacts {
  contours: Paged<RegistryContour>;
  /** Массивы, в которых у хозяйства есть земля. Порядок — первого появления. */
  massifsByActor: Map<number, string[]>;
  contoursByActor: Map<number, number>;
  contoursByMassif: Map<number, number>;
  actorsByMassif: Map<number, Set<number>>;
  mapped: number;
  arableMicroha: number;
  /** Контуры, у которых пашня не названа: их нет в сумме гектаров. */
  arableUnstated: number;
}

/**
 * Один справочник контуров на весь контекст анализа.
 *
 * Читают его четыре набора сразу; память живёт в `once`, ключом — контекст,
 * потому что смена даты среза меняет сам реестр. Справочник приходит ОДНИМ
 * ответом `/contours/index` (agroai-0e8.26, журнал №1627): прежний обход
 * `/contours` по 500 строк стоил 20 запросов и 5,6 МБ на каждое открытие
 * карты, реестра или карточки; значения те же (сверено построчно).
 */
function registryFacts(params: QueryParams): Promise<RegistryFacts> {
  return once(`contours:${contextKey(params)}`, async () => {
    const index = await api.contoursIndex(params);
    const items: RegistryContour[] = index.items.map((row) => ({
      ...row,
      cycle: row.cycle === null || row.cycle === undefined ? null : (index.cycles[row.cycle] ?? null),
    }));
    const contours: Paged<RegistryContour> = { items, total: index.total, truncated: false };
    const massifsByActor = new Map<number, string[]>();
    const contoursByActor = new Map<number, number>();
    const contoursByMassif = new Map<number, number>();
    const actorsByMassif = new Map<number, Set<number>>();
    let mapped = 0;
    let arableMicroha = 0;
    let arableUnstated = 0;
    for (const row of contours.items) {
      if (row.has_geometry) mapped += 1;
      // Неназванная пашня в сумму не входит; сколько строк промолчало —
      // считается отдельно, чтобы итог не выдавал незнание за ноль.
      if (row.arable_microha === null || row.arable_microha === undefined) arableUnstated += 1;
      else arableMicroha += row.arable_microha;
      contoursByMassif.set(row.massif_id, (contoursByMassif.get(row.massif_id) ?? 0) + 1);
      const user = row.land_user_id;
      if (user === null || user === undefined) continue;
      contoursByActor.set(user, (contoursByActor.get(user) ?? 0) + 1);
      const seen = massifsByActor.get(user) ?? [];
      const key = String(row.massif_id);
      if (!seen.includes(key)) seen.push(key);
      massifsByActor.set(user, seen);
      const actors = actorsByMassif.get(row.massif_id) ?? new Set<number>();
      actors.add(user);
      actorsByMassif.set(row.massif_id, actors);
    }
    return {
      contours,
      massifsByActor,
      contoursByActor,
      contoursByMassif,
      actorsByMassif,
      mapped,
      arableMicroha,
      arableUnstated,
    };
  });
}

function actorList(params: QueryParams): Promise<Paged<ActorRow>> {
  return once(`actors:${contextKey(params)}`, () =>
    pageThrough<ActorRow>((limit, offset) => api.actors({ ...params, limit, offset })),
  );
}

/* --------------------------------------------------------------- массивы */

async function readMassifs(params: QueryParams): Promise<Massif[]> {
  const [list, facts] = await Promise.all([api.massifs(params), registryFacts(params)]);
  return list.items.map((row) => ({
    id: String(row.id),
    name: row.canonical_name,
    area_ha: haOrOmit(row.total_microha),
    // Счётчик массива берётся из реестра контуров, а не из строки массива:
    // экран рядом фильтрует те же контуры, и два счёта разошлись бы молча.
    contours: facts.contoursByMassif.get(row.id) ?? row.contours,
    // Землепользователей массива считает СЕРВЕР — всех, с любой долей земли.
    // Счёт по реестру контуров видел только ПЕРВОГО землепользователя строки
    // и занижал массив (у массива 16: 41 против 49 канона).
    farms: row.actors,
    center: NO_CENTER,
  }));
}

/* ------------------------------------------------------------ хозяйства */

async function readFarms(params: QueryParams): Promise<Farm[]> {
  const [actors, facts] = await Promise.all([actorList(params), registryFacts(params)]);
  return actors.items.map((row) => ({
    id: String(row.id),
    name: row.name ?? tr(m.farmNo, { id: row.id }),
    massif_ids: facts.massifsByActor.get(row.id) ?? [],
    // Направление — сырая подпись источника, как её хранит канон. Пусто —
    // «направление не объявлено», а не «прочее».
    direction: row.directions[0]?.raw_label ?? '',
    area_ha: haOrOmit(row.total_microha),
    // Контуры хозяйства — все его землепользования (счёт канона, тот же, что
    // в кабинете хозяйства и на карточке), а не только строки реестра, где
    // оно названо первым: у хозяйства 22 это 663 против 326.
    contours: row.contours,
    // Руководителя хозяйства канон не хранит: подставить сюда имя было бы
    // изобретением лица, отвечающего за отчёт.
    head: '',
  }));
}

/* --------------------------------------------------------------- контуры */

/**
 * Название цикла словарём культур приложения («Пшеница 2026-2027»), а не
 * сырым именем справочника («Ғалла»): ключ цикла — «сезон:код культуры».
 * Одно правило на все подписи цикла — `cycleTitleOfKey`.
 */
const cycleTitleOf = cycleTitleOfKey;

async function readContours(params: QueryParams): Promise<ContourMeta[]> {
  const facts = await registryFacts(params);
  return facts.contours.items.map((row) => ({
    id: String(row.id),
    no: row.contour_no,
    massif_id: String(row.massif_id),
    // Землепользователей у контура может быть несколько; канон называет
    // основного, а число остальных едет рядом и печатается экраном.
    farm_id: row.land_user_id !== null && row.land_user_id !== undefined ? String(row.land_user_id) : '',
    land_users_count: row.land_users_count,
    ...(row.cycle
      ? {
          cycle: {
            status: row.cycle.status === 'one' || row.cycle.status === 'ambiguous' ? row.cycle.status : 'none',
            reason: row.cycle.reason ?? null,
            key: row.cycle.cycle_key ?? null,
            title: cycleTitleOf(row.cycle.cycle_key, row.cycle.cycle_title),
            crop_name: row.cycle.crop_name ?? null,
          },
        }
      : {}),
    area_ha: haOrOmit(row.arable_microha ?? row.total_microha),
    // Площадь названа источником? `false` — «0 га» на экране было бы
    // утверждением, которого канон не делал.
    area_stated: toHa(row.arable_microha ?? row.total_microha) !== null,
    category: UNKNOWN_CATEGORY,
    crop_2026: null,
    crop_2025w: null,
    geometry: row.has_geometry ? 'accepted' : 'none',
    center: NO_CENTER,
  }));
}

/* -------------------------------------------------------------- культуры */

function readCrops(): Crop[] {
  return canonCrops();
}

/* ----------------------------------------------------------------- опись */

async function readMeta(params: QueryParams): Promise<Meta> {
  const [summary, massifs, actors, facts] = await Promise.all([
    api.registrySummary(params),
    api.massifs(params),
    actorList(params),
    registryFacts(params),
  ]);
  const snapshot = summary.snapshot;
  return {
    // Треков у канона нет — поле осталось от прототипа и ни одним экраном не
    // читается; «A» здесь означает «набор один», а не «трек A».
    track: 'A',
    as_of: summary.context.as_of ?? snapshot?.state_date ?? '',
    district: districtName(),
    seed: 0,
    generated_at: snapshot?.published_at ?? '',
    sources: [
      ...(snapshot
        ? [tr(m.snapshot, { file: snapshot.source_filename, kind: snapshot.source_kind, rules: snapshot.rules_version })]
        : [tr(m.noSnapshot)]),
      // Оговорка едет рядом с итогом: гектары посчитаны по названным площадям.
      ...(facts.arableUnstated > 0
        ? [tr(m.arableUnstated, { count: facts.arableUnstated })]
        : []),
    ],
    totals: {
      massifs: massifs.items.length,
      farms: actors.total,
      contours: facts.contours.total,
      agri_area_ha: haOrOmit(facts.arableMicroha),
      mapped_contours: facts.mapped,
    },
  };
}

/* ------------------------------------------------------------------ циклы */

async function readCycles(params: QueryParams): Promise<Cycle[]> {
  const reply = await api.seasonCycles(params);
  return reply.items.map((row) => ({
    id: row.cycle,
    // Шаблона у районного цикла канона нет: цикл — это ПАРА «сезон × культура».
    template_id: '',
    crop: row.crop_code,
    season_label: row.season_label,
    status: row.status,
    window: { from: row.window_from ?? '', to: row.window_to ?? '' },
    farms: row.actors.total,
    contours: row.contours.total,
    // Сумма площадей цикла — агрегат сервера; неназванная в неё не входит.
    area_ha: haOrOmit(row.area.total_microha),
    stage_now: row.stage?.name ?? null,
    progress: {
      // «Работ» у цикла канона столько, сколько запущено кампаний; сколько из
      // них завершено — отдельный вопрос кампании, и ноль здесь был бы
      // выдуман, поэтому идут только измеренные сервером доли.
      works_total: row.campaigns,
      works_done: 0,
      works_active: 0,
      reported_pct: percent(row.obligations.reported, row.obligations.total),
      verified_pct: percent(row.obligations.verified, row.obligations.total),
    },
  }));
}

function percent(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 10000) / 100 : 0;
}

/* ------------------------------------------------------------ регистрация */

export const readRegistryDataset: DatasetReader = (name, params) => {
  switch (name) {
    case 'meta.json':
      return attempt(tr(m.whatMeta), () => readMeta(params));
    case 'massifs.json':
      return attempt(tr(m.whatMassifs), () => readMassifs(params));
    case 'farms.json':
      return attempt(tr(m.whatFarms), () => readFarms(params));
    case 'contours.meta.json':
      return attempt(tr(m.whatContours), () => readContours(params));
    case 'crops.json':
      return Promise.resolve<ReadResult<Crop[]>>({ ok: true, value: readCrops() });
    case 'cycles.json':
      return attempt(tr(m.whatCycles), () => readCycles(params));
    default:
      return null;
  }
};

/** Отдельно от чтения: пометка усечения нужна экранам реестра словами. */
export async function registryTruncated(params: QueryParams): Promise<boolean> {
  const facts = await registryFacts(params).catch(() => null);
  return facts?.contours.truncated ?? false;
}

export { failure as registryFailure };
